import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { categories, ctaButtons } from '@/mocks/categories';

export default function Hero() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategorySelect = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setDropdownOpen(false);
    setMobileMenuOpen(false);
  };

  const handleLinkClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <section className="relative w-full h-screen min-h-[700px] overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Minimalist%20fashion%20flat%20lay%20photography%20with%20neutral%20beige%20sneakers%20cashmere%20sweater%20and%20leather%20accessories%20arranged%20on%20cream%20linen%20fabric%20background%20soft%20natural%20window%20lighting%20from%20left%20side%20elegant%20editorial%20composition%20with%20organic%20textures%20and%20gentle%20shadows%20warm%20earthy%20tones%20clean%20aesthetic%20high%20end%20product%20styling&width=1920&height=1080&seq=hero-bg-css-01&orientation=landscape')`,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/35" />

      <nav className="absolute top-0 left-0 right-0 z-50 px-4 md:px-12 py-5 flex items-center justify-between">
        <Link to="/" className="font-heading text-xl md:text-3xl font-bold text-white tracking-wide whitespace-nowrap cursor-pointer">
          CSS Spreadsheet
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-3">
          {ctaButtons.map((btn) => (
            <a
              key={btn.name}
              href={btn.url}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full border border-white/40 text-white text-sm font-label font-medium hover:bg-white/15 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {btn.name}
            </a>
          ))}
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden flex items-center justify-center w-10 h-10 rounded-full border border-white/40 text-white hover:bg-white/15 transition-all duration-300 cursor-pointer"
          aria-label="Toggle navigation menu"
        >
          {mobileMenuOpen ? <i className="ri-close-line text-lg"></i> : <i className="ri-menu-line text-lg"></i>}
        </button>
      </nav>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="absolute inset-0 z-40 bg-black/70 backdrop-blur-sm flex flex-col">
          <div className="flex-1 overflow-y-auto px-6 pt-20 pb-8">
            {/* CTA buttons */}
            <div className="flex flex-col gap-3 mb-8">
              {ctaButtons.map((btn) => (
                <a
                  key={btn.name}
                  href={btn.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={handleLinkClick}
                  className="w-full px-5 py-3 rounded-full border border-white/40 text-white text-sm font-label font-medium text-center hover:bg-white/15 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  {btn.name}
                </a>
              ))}
            </div>

            {/* Categories grouped */}
            <div className="mb-8">
              <h3 className="text-white/50 text-xs font-label font-semibold uppercase tracking-widest mb-3">Clothing &amp; Footwear</h3>
              <div className="flex flex-col gap-1">
                {categories.slice(0, 9).map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleCategorySelect(cat.url)}
                    className="flex items-center gap-3 px-4 py-3 text-left text-white/90 hover:bg-white/10 rounded-xl transition-colors duration-150 cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center">
                      <i className={`${cat.icon} text-lg text-primary-400`}></i>
                    </span>
                    <span className="font-body text-sm">{cat.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-white/50 text-xs font-label font-semibold uppercase tracking-widest mb-3">Accessories &amp; Lifestyle</h3>
              <div className="flex flex-col gap-1">
                {categories.slice(9).map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleCategorySelect(cat.url)}
                    className="flex items-center gap-3 px-4 py-3 text-left text-white/90 hover:bg-white/10 rounded-xl transition-colors duration-150 cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center">
                      <i className={`${cat.icon} text-lg text-primary-400`}></i>
                    </span>
                    <span className="font-body text-sm">{cat.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-white/50 text-xs font-label font-semibold uppercase tracking-widest mb-3">Quick Links</h3>
              <Link to="/cssbuy-spreadsheet-guide" onClick={handleLinkClick} className="flex items-center gap-3 px-4 py-3 text-left text-white/90 hover:bg-white/10 rounded-xl transition-colors duration-150 cursor-pointer">
                <span className="w-8 h-8 flex items-center justify-center"><i className="ri-article-line text-lg text-primary-400"></i></span>
                <span className="font-body text-sm">CSSBuy Guide</span>
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="absolute inset-0 flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-3xl text-center">
          <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-extrabold text-white leading-tight mb-6">
            Find Your
            <br />
            Perfect Style
            <br />
            <span className="italic font-normal">Effortlessly</span>
          </h1>

          <p className="text-white/80 text-lg md:text-xl font-body mb-10 max-w-xl mx-auto">
            The most comprehensive CSSBuy spreadsheet hub — curated fashion finds, verified links, and daily updated shopping resources all in one place.
          </p>

          <div ref={dropdownRef} className="relative inline-block">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-3 px-8 py-4 bg-white/15 backdrop-blur-md border border-white/30 rounded-full text-white font-label font-medium text-base hover:bg-white/25 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-grid-line text-lg"></i>
              Browse Categories
              {dropdownOpen ? <i className="ri-arrow-up-s-line"></i> : <i className="ri-arrow-down-s-line"></i>}
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 w-72 bg-white/95 backdrop-blur-xl rounded-2xl overflow-hidden z-50 max-h-80 overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleCategorySelect(cat.url)}
                    className="w-full flex items-center gap-3 px-5 py-3 text-left text-foreground-800 hover:bg-background-100 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center">
                      <i className={`${cat.icon} text-lg text-primary-500`}></i>
                    </span>
                    <span className="font-body text-sm">{cat.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 pb-8 text-center">
        <p className="font-heading text-6xl md:text-8xl lg:text-9xl font-black text-white/20 tracking-widest select-none">
          FIND YOUR STYLE
        </p>
      </div>
    </section>
  );
}